
import React from "react";
import ReactDOM from "react-dom/client";
import * as Hooks from "../../../../../hooks";
import { i18nManager } from "../../../../../i18n";
import { Schemes, Utils } from "../../../../../utils";
import { ConfirmDialog } from "../../../../shared/ConfirmDialog";
import { Button, Checkbox, Dropdown, Form, FormControl, ObjectHelper, TextAreaInput, TextInput } from "@rivtn/noseur";
import { ITournamentEditionCustomField, TournamentService, TournamentCustomFieldType, BaseService, SanitizedResponse } from "../../../../../services";
import { CreateTournamentSectionProps, CreateTournamentSection } from "../CreateTournament";
import { Elements } from "../../../../shared/Elements";

function CustomFields(props: CreateTournamentSectionProps) {

    const { active, tournamentEditionState } = props;
    const [themeMode, _] = Hooks.useThemeMode();
    const tournamentService = TournamentService.getInstance();
    const labels = i18nManager.Labels.host.create_tournament.custom_fields;
    const [tournamentEdition, setTournamentEdition] = tournamentEditionState;
    const [customFields, setCustomFields] = React.useState<ITournamentEditionCustomField[] | undefined>(tournamentEdition.___custom_fields___ ?? undefined);

    React.useEffect(fetchInformations, []);

    return (<div className="create-tournament-f flow details" ref={props.ref}>
        {!active && (<div className="overlay" onClick={() => props.setActiveSection(CreateTournamentSection.CUSTOM_FIELDS, undefined, true)} />)}
        <span className="title">{labels.title}</span>
        <Form className="content" scheme={Schemes.OMNEHOST} style={{ marginTop: 10, width: "100%" }}>
            {(!customFields) && (Elements.LOADING_STATE)}
            {(customFields) && (customFields.map((customField, index) => {
                return (<div key={customField.label}>
                    <i className="fa fa-times link danger" style={{ fontSize: 20, marginRight: 10 }} onClick={() => {
                        customFields.splice(customFields.indexOf(customField), 1);
                        tournamentService.deleteTournamentEditionCustomField(tournamentEdition.external_id!, customField.external_id).then(({ }: SanitizedResponse<ITournamentEditionCustomField>) => {
                            setCustomFields([...customFields]);
                            setTournamentEdition({ ...tournamentEdition, ___custom_fields___: customFields });
                        }).catch(BaseService.reportError);
                        return false;
                    }} />
                    <i className="fa fa-pen link" style={{ fontSize: 20, marginRight: 10 }} onClick={() => {
                        addOrEditCustomField(index, customField);
                    }} />
                    <span style={{ marginRight: 10, fontSize: 12 }}>({(labels as any)[customField.type.toLowerCase()] ?? labels.text})</span>
                    <span>{customField.label} {customField.required && (<span style={{ color: "#FF7171" }}>*</span>)}</span>
                </div>);
            }))}
            <span className="link" style={{ marginTop: 30 }} onClick={() => addOrEditCustomField()}>{labels.add_new_custom_field}</span>
        </Form>
        <div className="controls">
            <Button textOnly scheme={Schemes.OMNEHOST} text={i18nManager.Labels.common.prev} loadingProps={Elements.BUTTON_LOADING_PROPS} onClick={(e) => {
                e.manageRef?.setLoadingState(true);
                props.setActiveSection(CreateTournamentSection.REGISTRATION, () => {
                    e.manageRef?.setLoadingState(false);
                });
            }} />
            <Button scheme={Schemes.OMNEHOST} text={i18nManager.Labels.common.next} loadingProps={Elements.BUTTON_LOADING_PROPS} onClick={(e) => {
                e.manageRef?.setLoadingState(true);
                props.setActiveSection(CreateTournamentSection.PRIZE_POOL, () => {
                    e.manageRef?.setLoadingState(false);
                });
            }} />
        </div>
    </div>);

    function fetchInformations() {
        tournamentService.getTournamentEditionCustomFields(tournamentEdition.external_id!).then(({ content }: SanitizedResponse<ITournamentEditionCustomField>) => {
            setCustomFields(content);
        }).catch(BaseService.reportError);
    }

    function addOrEditCustomField(index: number = 0, customField: ITournamentEditionCustomField = { required: false, } as any) {
        const isNew = !customField.external_id;
        const dialogLabels = labels.dialog.add_edit;
        const dialogErrorRef = { current: null } as any;
        const optionsMutableRoot = { current: null } as unknown as React.RefObject<ReactDOM.Root>;

        function renderOptions() {
            if (!optionsMutableRoot.current) return;
            if (customField.type !== TournamentCustomFieldType.OPTIONS) {
                optionsMutableRoot.current.render(undefined);
                return;
            }
            optionsMutableRoot.current.render(<>
                <p style={{ fontWeight: "bold", marginTop: 25 }}>{dialogLabels.options}</p>
                {customField.options?.map((option, subIndex) => {
                    return (<div key={option + subIndex} style={{ display: "flex", alignItems: "center", marginBottom: 15 }}>
                        <i className="fa fa-times link danger" style={{ fontSize: 20, marginRight: 10 }} onClick={() => {
                            customField.options!.splice(customField.options!.indexOf(option), 1);
                            renderOptions();
                        }} />
                        <TextInput fill scheme={Schemes.OMNEHOST} defaultValue={option} onInput={({ target: { value } }: any) => {
                            customField.options![subIndex] = value;
                        }} />
                    </div>);
                })}
                <p className="link" onClick={() => {
                    if (!customField.options) customField.options = [];
                    customField.options!.push("");
                    renderOptions();
                }}>{dialogLabels.add_new_option}</p>
            </>);
        }

        const dialog = ConfirmDialog({
            closable: true,
            style: { minWidth: 400 },
            confirmLabel: (isNew ? dialogLabels.add : dialogLabels.update),
            title: (isNew ? dialogLabels.add_new_custom_field : dialogLabels.update_custom_field),
            content: (<Form scheme={Schemes.OMNEHOST} style={{ width: "100%", marginTop: 10 }}>
                <Dropdown fill formControlProps={{ className: "plan-dd", isFieldset: true, label: dialogLabels.type, }}
                    selectedOptionIndex={Object.values(TournamentCustomFieldType).findIndex((o) => o === customField.type)} options={Object.values(TournamentCustomFieldType).map((value) => ({
                        value,
                        label: Utils.toSaneSentenceFormat((labels as any)[value.toLowerCase()] ?? labels.text),
                    }))} onSelectOption={(o) => {
                        dialogErrorRef.current.innerText = " ";
                        customField.type = o.value;
                        renderOptions();
                    }} />
                <FormControl isFieldset fill label={dialogLabels.label} style={{ marginTop: 15 }}>
                    <TextAreaInput fill style={{ height: 150 }} defaultValue={customField.label} onInput={({ target: { value } }: any) => {
                        dialogErrorRef.current.innerText = " ";
                        customField.label = value;
                    }} />
                </FormControl>
                <Checkbox defaultChecked={customField.required} label={dialogLabels.required} style={{ marginTop: 20 }} onChecked={(checked) => {
                    customField.required = checked;
                }} />
                <div ref={(r: any) => {
                    if (!r) return;
                    ObjectHelper.resolveRef(optionsMutableRoot, ReactDOM.createRoot(r));
                    renderOptions();
                }} />
                <div ref={dialogErrorRef} className="error"> </div>
            </Form>),
            onConfirm: async (b) => {
                if (!customField.type || !customField.label || (customField.type === TournamentCustomFieldType.OPTIONS && !customField.options?.length)) {
                    dialogErrorRef.current.innerText = dialogLabels.missing_fields;
                    return false;
                }
                b?.setLoadingState(true);
                let newCustomFields = (customFields ?? []);
                if (isNew) {
                    newCustomFields = [...(customFields ?? []), customField];
                } else {
                    newCustomFields[index] = customField;
                }
                (isNew ? tournamentService.createTournamentEditionCustomField : tournamentService.updateTournamentEditionCustomField).bind(tournamentService)(tournamentEdition.external_id!, customField).then(({ }: SanitizedResponse<ITournamentEditionCustomField>) => {
                    dialog.hide(fetchInformations);
                    setTournamentEdition({ ...tournamentEdition, ___custom_fields___: customFields });
                }).catch(BaseService.reportError).finally(() => b?.setLoadingState(false));
                return false;
            }
        });
        dialog.show();
    }

}

export default CustomFields;
